// MarkerAdder.js — add a reference point from a pasted Google Maps link or a raw lat/lon.
// Reuses KmlSetup.parseLocation so there is exactly ONE coordinate parser in the app.

import React, { useState } from 'react';
import { parseLocation } from './KmlSetup';

export default function MarkerAdder({ frame, markers, onAdd, onRemove }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [label, setLabel] = useState('');

  const loc = parseLocation(text);
  // A coordinate outside the canvas would be parsed fine but drawn off-screen, which reads
  // as "nothing happened". Catch it here and say so.
  let outside = false;
  if (loc && frame && frame.lonLatToPixel) {
    const [x, y] = frame.lonLatToPixel(loc.lon, loc.lat);
    outside = x < 0 || y < 0 || x > frame.width || y > frame.height;
  }
  const canAdd = Boolean(loc) && !outside;

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} style={pill} title="Add a reference point from lat/lon">
        ⊕ Point{markers.length ? ` (${markers.length})` : ''}
      </button>
    );
  }

  return (
    <div style={panel}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <b style={{ fontSize: 13 }}>Reference point</b>
        <button onClick={() => setOpen(false)} style={x}>×</button>
      </div>

      <input value={text} onChange={(e) => setText(e.target.value)}
             placeholder="41.6814, -93.7102  — or a Google Maps link" style={input} />
      <input value={label} onChange={(e) => setLabel(e.target.value)}
             placeholder="label (optional)" style={{ ...input, marginTop: 6 }} />

      {text && !loc && <div style={bad}>Could not read a coordinate from that.</div>}
      {loc && outside && (
        <div style={bad}>
          That point is outside the current canvas. Enlarge the canvas to include it.
        </div>
      )}
      {canAdd && <div style={ok}>✓ {loc.lat.toFixed(6)}, {loc.lon.toFixed(6)}</div>}

      <button
        disabled={!canAdd}
        onClick={() => { onAdd({ lon: loc.lon, lat: loc.lat, label: label.trim() }); setText(''); setLabel(''); }}
        style={{ ...btn, background: canAdd ? '#0a84ff' : '#3a3a3c', cursor: canAdd ? 'pointer' : 'default' }}
      >
        Add point
      </button>

      {markers.length > 0 && (
        <div style={{ marginTop: 10, maxHeight: 130, overflowY: 'auto' }}>
          {markers.map((m) => (
            <div key={m.id} style={row}>
              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {m.label || `${m.lat.toFixed(5)}, ${m.lon.toFixed(5)}`}
              </span>
              <button onClick={() => onRemove(m.id)} style={del}>×</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const base = { fontFamily: 'system-ui, Arial, sans-serif', color: '#f2f2f7' };
const pill = { ...base, position: 'absolute', left: 12, bottom: 52, zIndex: 1200, background: 'rgba(28,28,30,.92)', border: '1px solid #3a3a3c', borderRadius: 999, padding: '7px 13px', fontSize: 12, cursor: 'pointer' };
const panel = { ...base, position: 'absolute', left: 12, bottom: 52, zIndex: 1200, width: 250, background: 'rgba(28,28,30,.97)', border: '1px solid #3a3a3c', borderRadius: 10, padding: 12, boxShadow: '0 10px 30px rgba(0,0,0,.5)' };
const input = { width: '100%', boxSizing: 'border-box', background: '#2c2c2e', color: '#f2f2f7', border: '1px solid #48484a', borderRadius: 6, padding: 7, fontSize: 12 };
const btn = { width: '100%', marginTop: 9, border: 'none', borderRadius: 7, padding: '8px 0', color: 'white', fontSize: 12 };
const row = { display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, padding: '3px 0', borderTop: '1px solid #333' };
const del = { background: 'transparent', border: 'none', color: '#ff6b6b', cursor: 'pointer', fontSize: 14, padding: 0 };
const x = { background: 'transparent', border: 'none', color: '#f2f2f7', fontSize: 17, cursor: 'pointer', lineHeight: 1, padding: 0 };
const ok = { marginTop: 6, color: '#30d158', fontSize: 11 };
const bad = { marginTop: 6, color: '#ff6b6b', fontSize: 11, lineHeight: 1.4 };
